/**
 * Conflict Resolution Service
 * Handles merging of local and server records with configurable strategies
 */

import * as Effect from 'effect/Effect';
import * as Context from 'effect/Context';
import * as Layer from 'effect/Layer';
import type { TransactionRecord } from '../storage/storage.service';
import { Logger } from '../logger/logger.service';
import { Clock } from '../clock/clock.service';

// Conflict representation
export interface Conflict<T> {
  readonly recordId: string;
  readonly recordType: 'transaction' | 'group' | 'category';
  readonly local: T;
  readonly remote: T;
  readonly localModified: string;
  readonly remoteModified: string;
}

// Resolution result
export type ResolutionResult<T> =
  | AutoResolved<T>
  | NeedsUserInput<T>;

export interface AutoResolved<T> {
  readonly _tag: 'AutoResolved';
  readonly recordId: string;
  readonly resolved: T;
  readonly strategy: ResolutionStrategy;
}

export interface NeedsUserInput<T> {
  readonly _tag: 'NeedsUserInput';
  readonly recordId: string;
  readonly conflict: Conflict<T>;
  readonly reason: string;
}

// Resolution strategies
export type ResolutionStrategy =
  | 'ServerWins'        // Server version takes precedence
  | 'LocalWins'         // Local version takes precedence
  | 'MostRecent'        // Use most recently modified
  | 'FieldMerge'        // Merge non-conflicting fields
  | 'UserDecision';     // Cannot auto-resolve, needs user

// Conflict resolver interface
export interface ConflictResolverService {
  readonly resolveTransaction: (
    conflict: Conflict<TransactionRecord>
  ) => Effect.Effect<ResolutionResult<TransactionRecord>, never>;

  readonly resolveTransactions: (
    conflicts: readonly Conflict<TransactionRecord>[]
  ) => Effect.Effect<
    {
      readonly autoResolved: readonly AutoResolved<TransactionRecord>[];
      readonly needsUserInput: readonly NeedsUserInput<TransactionRecord>[];
    },
    never
  >;

  readonly applyUserResolution: <T>(
    conflictId: string,
    chosenVersion: 'local' | 'remote' | 'custom',
    customValue?: T
  ) => Effect.Effect<T, Error>;
}

export class ConflictResolver extends Context.Tag('ConflictResolver')<
  ConflictResolver,
  ConflictResolverService
>() {}

// Helper functions for conflict resolution
function canAutoResolve<T extends { updated_at?: string; created_at: string }>(
  conflict: Conflict<T>
): ResolutionStrategy | null {
  const localTime = new Date(conflict.local.updated_at || conflict.local.created_at).getTime();
  const remoteTime = new Date(conflict.remote.updated_at || conflict.remote.created_at).getTime();

  // If deleted on server but modified locally, needs user decision
  if ('deleted' in conflict.remote && conflict.remote.deleted && conflict.local.updated_at) {
    return null; // User must decide: keep local changes or accept deletion
  }

  // If deleted locally but modified on server, needs user decision
  if ('deleted' in conflict.local && conflict.local.deleted && conflict.remote.updated_at) {
    return null; // User must decide: keep deletion or accept server changes
  }

  // If both have same content but different timestamps, use most recent
  if (areRecordsEqual(conflict.local, conflict.remote)) {
    return 'MostRecent';
  }

  // If local is newer by more than 1 second, local wins
  if (localTime > remoteTime + 1000) {
    return 'LocalWins';
  }

  // If remote is newer by more than 1 second, server wins
  if (remoteTime > localTime + 1000) {
    return 'ServerWins';
  }

  // If modified within 1 second of each other, try field merge
  return tryFieldMerge(conflict) ? 'FieldMerge' : null;
}

function areRecordsEqual<T>(a: T, b: T): boolean {
  // Simple equality check for primitives and objects
  return JSON.stringify(a) === JSON.stringify(b);
}

function tryFieldMerge<T extends Record<string, any>>(conflict: Conflict<T>): boolean {
  // Check if we can merge by comparing fields
  const localKeys = Object.keys(conflict.local);
  const remoteKeys = Object.keys(conflict.remote);

  // If different structure, cannot merge
  if (localKeys.length !== remoteKeys.length) {
    return false;
  }

  // Count conflicting fields
  let conflictingFields = 0;
  for (const key of localKeys) {
    if (conflict.local[key] !== conflict.remote[key]) {
      // Ignore timestamp fields for conflict counting
      if (key !== 'updated_at' && key !== 'created_at') {
        conflictingFields++;
      }
    }
  }

  // If more than 2 fields differ (excluding timestamps), don't auto-merge
  return conflictingFields <= 2;
}

function mergeRecords<T extends Record<string, any>>(
  local: T,
  remote: T,
  strategy: ResolutionStrategy
): T {
  switch (strategy) {
    case 'ServerWins':
      return remote;
    case 'LocalWins':
      return local;
    case 'MostRecent': {
      const localTime = new Date(local.updated_at || local.created_at).getTime();
      const remoteTime = new Date(remote.updated_at || remote.created_at).getTime();
      return remoteTime > localTime ? remote : local;
    }
    case 'FieldMerge': {
      // Merge fields, preferring more recent values for each field
      const merged: Record<string, any> = { ...remote };
      const localObj = local as Record<string, any>;
      const remoteObj = remote as Record<string, any>;

      for (const key of Object.keys(localObj)) {
        // For non-timestamp fields, prefer local if it's set and remote isn't
        if (key !== 'updated_at' && key !== 'created_at') {
          if (localObj[key] != null && remoteObj[key] == null) {
            merged[key] = localObj[key];
          }
        }
      }
      return merged as T;
    }
    default:
      return remote; // Fallback to server wins
  }
}

// Live implementation
export const ConflictResolverLive = Layer.effect(
  ConflictResolver,
  Effect.gen(function* () {
    const logger = yield* Logger;
    const clock = yield* Clock;

    return {
      resolveTransaction: (conflict) =>
        Effect.gen(function* () {
          yield* logger.debug('conflict', 'Resolving transaction conflict', {
            recordId: conflict.recordId,
            localModified: conflict.localModified,
            remoteModified: conflict.remoteModified,
          });

          const strategy = canAutoResolve(conflict);

          if (strategy === null) {
            yield* logger.info('conflict', 'Conflict requires user input', {
              recordId: conflict.recordId,
            });

            return {
              _tag: 'NeedsUserInput' as const,
              recordId: conflict.recordId,
              conflict,
              reason: 'Conflicting changes require manual resolution',
            };
          }

          const resolved = mergeRecords(conflict.local, conflict.remote, strategy);

          yield* logger.info('conflict', 'Auto-resolved transaction conflict', {
            recordId: conflict.recordId,
            strategy,
          });

          return {
            _tag: 'AutoResolved' as const,
            recordId: conflict.recordId,
            resolved,
            strategy,
          };
        }),

      resolveTransactions: (conflicts) =>
        Effect.gen(function* () {
          const timestamp = yield* clock.isoString();

          yield* logger.info('conflict', 'Resolving multiple conflicts', {
            count: conflicts.length,
            timestamp,
          });

          const results = yield* Effect.all(
            conflicts.map(conflict =>
              Effect.gen(function* () {
                return yield* Effect.gen(function* () {
                  const strategy = canAutoResolve(conflict);

                  if (strategy === null) {
                    return {
                      _tag: 'NeedsUserInput' as const,
                      recordId: conflict.recordId,
                      conflict,
                      reason: 'Conflicting changes require manual resolution',
                    };
                  }

                  const resolved = mergeRecords(conflict.local, conflict.remote, strategy);

                  return {
                    _tag: 'AutoResolved' as const,
                    recordId: conflict.recordId,
                    resolved,
                    strategy,
                  };
                });
              })
            )
          );

          const autoResolved = results.filter(
            (r): r is AutoResolved<TransactionRecord> => r._tag === 'AutoResolved'
          );
          const needsUserInput = results.filter(
            (r): r is NeedsUserInput<TransactionRecord> => r._tag === 'NeedsUserInput'
          );

          yield* logger.info('conflict', 'Conflict resolution complete', {
            autoResolved: autoResolved.length,
            needsUserInput: needsUserInput.length,
          });

          return { autoResolved, needsUserInput };
        }),

      applyUserResolution: (conflictId, chosenVersion, customValue) =>
        Effect.gen(function* () {
          yield* logger.info('conflict', 'Applying user resolution', {
            conflictId,
            chosenVersion,
            hasCustomValue: !!customValue,
          });

          if (chosenVersion === 'custom' && !customValue) {
            return yield* Effect.fail(new Error('Custom value required for custom resolution'));
          }

          if (chosenVersion === 'custom' && customValue) {
            return customValue;
          }

          // For local/remote choices, this would need to access the stored conflict
          // In a full implementation, we'd maintain a conflict store
          return yield* Effect.fail(new Error('Not implemented: need conflict store'));
        }),
    };
  })
);
